<template>
  <div class="min-h-screen bg-[#f6f7f9] flex items-center justify-center px-6 py-16">
    <div class="max-w-[520px] w-full bg-white rounded-2xl border border-[#e9eaec] shadow-sm p-8 sm:p-10 text-center">

      <!-- Bezig met controleren -->
      <template v-if="loading">
        <div class="w-12 h-12 rounded-full border-4 border-[#f0f0f0] border-t-[#FFD500] animate-spin mx-auto mb-5"></div>
        <p class="text-[#888]">Betaling controleren…</p>
      </template>

      <!-- Betaald (fallback - normaal sturen we door naar /thank-you) -->
      <template v-else-if="paid">
        <div class="w-16 h-16 rounded-full bg-[#e9f9ef] flex items-center justify-center mx-auto mb-5">
          <i class="bi bi-check-lg text-[2rem] text-[#1db954]"></i>
        </div>
        <h1 class="text-[1.5rem] font-extrabold text-[#111] mb-2">Betaling gelukt</h1>
        <p class="text-[#666] mb-6">Bedankt voor je bestelling{{ order?.number ? ` #${order.number}` : '' }}!</p>
        <router-link :to="`/thank-you?key=${encodeURIComponent(key)}`" class="btn-primary">
          Bekijk je bestelling
        </router-link>
      </template>

      <!-- Nog in behandeling (webhook loopt mogelijk achter) -->
      <template v-else-if="pending">
        <div class="w-16 h-16 rounded-full bg-[#eef4ff] flex items-center justify-center mx-auto mb-5">
          <i class="bi bi-hourglass-split text-[1.7rem] text-[#3b6fd4]"></i>
        </div>
        <h1 class="text-[1.5rem] font-extrabold text-[#111] mb-2">Betaling wordt verwerkt</h1>
        <p class="text-[#666] mb-6">
          We hebben je betaling nog niet bevestigd voor bestelling{{ order?.number ? ` #${order.number}` : '' }}.
          Heb je net betaald? Dan kan dit een moment duren - vernieuw de pagina.
        </p>
        <button class="btn-primary w-full mb-3" @click="reload">
          <i class="bi bi-arrow-clockwise"></i> Vernieuwen
        </button>
        <button class="btn-ghost w-full" :disabled="retrying" @click="retryPayment">
          <i class="bi bi-credit-card"></i> Toch opnieuw betalen
        </button>
      </template>

      <!-- Niet afgerond / geannuleerd -->
      <template v-else>
        <div class="w-16 h-16 rounded-full bg-[#fff4e5] flex items-center justify-center mx-auto mb-5">
          <i class="bi bi-exclamation-lg text-[2rem] text-[#f5a623]"></i>
        </div>
        <h1 class="text-[1.5rem] font-extrabold text-[#111] mb-2">Betaling niet afgerond</h1>
        <p class="text-[#666] mb-6">
          Je betaling is geannuleerd of niet voltooid. Geen zorgen - je bestelling{{ order?.number ? ` #${order.number}` : '' }}
          staat voor je klaar en je winkelwagen is bewaard. Je kunt het meteen opnieuw proberen.
        </p>

        <p v-if="error" class="text-[#d33] text-[0.875rem] mb-4">{{ error }}</p>

        <button class="btn-primary w-full mb-3" :disabled="retrying" @click="retryPayment">
          <span v-if="retrying" class="spin"><i class="bi bi-arrow-repeat"></i></span>
          <i v-else class="bi bi-credit-card"></i>
          {{ retrying ? 'Bezig…' : 'Opnieuw betalen' }}
        </button>
        <router-link to="/cart" class="btn-ghost w-full">
          <i class="bi bi-cart"></i> Terug naar winkelwagen
        </router-link>
      </template>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route   = useRoute()
const router  = useRouter()
const loading = ref(true)
const paid    = ref(false)
const pending = ref(false)
const order   = ref(null)
const retrying = ref(false)
const error   = ref('')
const key     = route.query.key || ''

// WooCommerce-statussen die als "betaald" gelden
const PAID_STATUSES   = ['processing', 'completed', 'on-hold']
const FAILED_STATUSES = ['cancelled', 'failed', 'refunded']

async function fetchOrder() {
  const res = await fetch(`/api/order/by-key?key=${encodeURIComponent(key)}`)
  if (!res.ok) throw new Error('order-fetch')
  return res.json()
}

onMounted(async () => {
  if (!key || typeof key !== 'string' || !key.startsWith('wc_order_')) {
    loading.value = false
    error.value = 'Geen geldige bestelling gevonden.'
    return
  }
  try {
    let o = await fetchOrder()
    order.value = o
    if (PAID_STATUSES.includes(o.status)) {
      return router.replace(`/thank-you?key=${encodeURIComponent(key)}`)
    }
    // Mollie stuurt je vaak terug vóórdat de betaal-webhook de order bijwerkt.
    // Blijf ~12s pollen zodat een geslaagde betaling niet ten onrechte als
    // "geannuleerd" wordt getoond.
    let tries = 0
    while (o.status === 'pending' && tries < 6) {
      await new Promise(r => setTimeout(r, 2000))
      tries++
      try { o = await fetchOrder(); order.value = o } catch {}
      if (PAID_STATUSES.includes(o.status)) {
        return router.replace(`/thank-you?key=${encodeURIComponent(key)}`)
      }
      if (FAILED_STATUSES.includes(o.status)) break
    }
    pending.value = (o.status === 'pending')
  } catch (e) {
    error.value = 'Er ging iets mis bij het controleren van je betaling.'
  } finally {
    loading.value = false
  }
})

function reload() {
  if (typeof window !== 'undefined') window.location.reload()
}

async function retryPayment() {
  if (!order.value) return
  retrying.value = true
  error.value = ''
  try {
    const res = await fetch('/api/payment/initiate', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ order_id: order.value.id, order_key: key }),
    })
    const data = await res.json()
    if (res.ok && data.redirect) {
      window.location.href = data.redirect
    } else {
      error.value = data.message || 'Betaling opnieuw starten mislukt. Probeer het via de winkelwagen.'
    }
  } catch (e) {
    error.value = 'Verbindingsfout. Probeer het opnieuw.'
  } finally {
    retrying.value = false
  }
}
</script>

<style scoped>
.btn-primary {
  display: inline-flex; align-items: center; justify-content: center; gap: .5rem;
  background: #FFD500; color: #111; font-weight: 700; font-size: .95rem;
  padding: .8125rem 1.5rem; border-radius: 8px; border: none; cursor: pointer;
  text-decoration: none; transition: background .15s;
}
.btn-primary:hover:not(:disabled) { background: #ffb800; }
.btn-primary:disabled { opacity: .6; cursor: default; }
.btn-ghost {
  display: inline-flex; align-items: center; justify-content: center; gap: .5rem;
  background: #f5f6f7; color: #333; font-weight: 600; font-size: .9rem;
  padding: .75rem 1.5rem; border-radius: 8px; text-decoration: none; transition: background .15s;
}
.btn-ghost:hover { background: #ececee; }
.spin { display: inline-block; animation: bi-spin .8s linear infinite; }
@keyframes bi-spin { to { transform: rotate(360deg); } }
</style>
